import { z } from 'zod'
import { PROVIDER_IDS, ProviderId, SuggestionMode } from '../enums'
import { Ratio } from './primitives'

export const TENANT_NAME_MAX_CHARS = 200
export const CLAIM_IDLE_MINUTES_MAX = 1440
export const MONTHLY_TOKEN_BUDGET_MAX = 1_000_000_000
export const TOKEN_PRICE_MAX = 1000
export const EMBEDDING_PRICE_KEY = 'embedding'

export const TOKEN_PRICE_KEYS = [...PROVIDER_IDS, EMBEDDING_PRICE_KEY] as const
export const TokenPriceKey = z.enum(TOKEN_PRICE_KEYS)
export type TokenPriceKey = z.infer<typeof TokenPriceKey>

export const TokenPrices = z.partialRecord(TokenPriceKey, z.number().min(0).max(TOKEN_PRICE_MAX))
export type TokenPrices = z.infer<typeof TokenPrices>

const sortedPriceEntries = (prices: TokenPrices): string =>
  JSON.stringify(Object.entries(prices).sort(([a], [b]) => a.localeCompare(b)))

export const sameTokenPrices = (a: TokenPrices, b: TokenPrices): boolean => sortedPriceEntries(a) === sortedPriceEntries(b)

export const SAME_PROVIDER_TWICE_MESSAGE = '備援 AI provider 不可以和主要 provider 相同，否則主要 provider 出事時不會有備援'
export const PROVIDER_KEY_MISSING_MESSAGE = '請先到「AI 供應商」填入這個 provider 的金鑰，才可以選用'
export const PROVIDER_NOT_OFFERED_MESSAGE = '這個 AI provider 在目前的環境沒有設定，選了也無法使用'
export const TENANT_NAME_REQUIRED_MESSAGE = '公司名稱不可以留空'

export const EXPECTED_PRICES_WITHOUT_PRICES_MESSAGE = '比對用的原始單價只能和新的單價一起送出'

const SETTINGS_REFUSALS: readonly string[] = [
  TENANT_NAME_REQUIRED_MESSAGE,
  SAME_PROVIDER_TWICE_MESSAGE,
  EXPECTED_PRICES_WITHOUT_PRICES_MESSAGE,
]

export const settingsRefusalOf = (error: z.ZodError): string | null =>
  error.issues.map((issue) => issue.message).find((message) => SETTINGS_REFUSALS.includes(message)) ?? null

export const isSameProviderTwice = (
  primary: ProviderId | undefined,
  secondary: ProviderId | null | undefined,
): boolean => primary !== undefined && secondary !== undefined && secondary !== null && primary === secondary

const MonthlyTokenBudget = z.number().int().min(0).max(MONTHLY_TOKEN_BUDGET_MAX)

export const OperatorSettings = z.object({
  monthlyTokenBudget: z.number().int().nonnegative(),
  retentionDays: z.number().int().positive(),
  llmConcurrency: z.number().int().positive(),
})
export type OperatorSettings = z.infer<typeof OperatorSettings>

export const OperatorTermsInput = OperatorSettings.extend({ monthlyTokenBudget: MonthlyTokenBudget })
export type OperatorTermsInput = z.infer<typeof OperatorTermsInput>

export const TenantOperatorTerms = OperatorSettings.omit({ monthlyTokenBudget: true })
export type TenantOperatorTerms = z.infer<typeof TenantOperatorTerms>

export const TenantSettings = z.object({
  name: z.string().min(1),
  suggestionMode: SuggestionMode,
  primaryProvider: ProviderId,
  secondaryProvider: ProviderId.nullable(),
  claimIdleMinutes: z.number().int().positive(),
  answerabilityThreshold: Ratio,
  replyRequiresClaim: z.boolean(),
  monthlyTokenBudget: z.number().int().nonnegative(),
  tokenPrices: TokenPrices,
  operatorFields: TenantOperatorTerms,
  providerChoices: z.array(ProviderId),
})
export type TenantSettings = z.infer<typeof TenantSettings>

export const TenantSettingsPatchRequest = z
  .strictObject({
    name: z.string().trim().min(1, TENANT_NAME_REQUIRED_MESSAGE).max(TENANT_NAME_MAX_CHARS).optional(),
    suggestionMode: SuggestionMode.optional(),
    primaryProvider: ProviderId.optional(),
    secondaryProvider: ProviderId.nullable().optional(),
    claimIdleMinutes: z.number().int().min(1).max(CLAIM_IDLE_MINUTES_MAX).optional(),
    answerabilityThreshold: Ratio.optional(),
    replyRequiresClaim: z.boolean().optional(),
    monthlyTokenBudget: MonthlyTokenBudget.optional(),
    tokenPrices: TokenPrices.optional(),
    expectedTokenPrices: TokenPrices.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'at least one field is required' })
  .refine((patch) => !isSameProviderTwice(patch.primaryProvider, patch.secondaryProvider), {
    message: SAME_PROVIDER_TWICE_MESSAGE,
    path: ['secondaryProvider'],
  })
  .refine((patch) => patch.expectedTokenPrices === undefined || patch.tokenPrices !== undefined, {
    message: EXPECTED_PRICES_WITHOUT_PRICES_MESSAGE,
    path: ['expectedTokenPrices'],
  })
export type TenantSettingsPatchRequest = z.infer<typeof TenantSettingsPatchRequest>
